import React, { useEffect, useState } from "react";
import useDashboardData from "../hooks/useDashboardData";
import Chart from "./Chart";
import "./Dashboard.css";

function ProfitDashboard() {
  const {
    data: chartsData,
    loading,
    error,
    refetch,
  } = useDashboardData("/visualize/profit", { method: "POST" });
  const [stdSummary, setStdSummary] = useState(null);

  useEffect(() => {
    fetchStandardSummary();
  }, []);

  const fetchProfitData = refetch;

  const fetchStandardSummary = async () => {
    try {
      const res = await fetch(
        "https://restaurant-analytics-backend.onrender.com/report/summary",
        {
          method: "GET",
        }
      );
      if (res.status === 404) return; // No summary yet
      const data = await res.json();
      if (res.ok) {
        setStdSummary(data);
      } else {
        throw new Error(data.detail || "Failed to fetch standardized summary");
      }
    } catch (err) {
      console.error(
        "Error fetching standardized summary:",
        err.message || String(err)
      );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Profit Analytics...</h2>
        <p>Analyzing profit margins and generating financial insights</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>❌ Error Loading Profit Data</h2>
        <p>{error}</p>
        <p>
          Make sure you have uploaded both sales data and product costs for
          profit analysis.
        </p>
        <button onClick={fetchProfitData} className="retry-btn">
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!chartsData || !chartsData.charts) {
    return (
      <div className="dashboard-error">
        <h2>💰 No Profit Data Available</h2>
        <p>
          Please upload your sales data and product costs first to view profit
          analytics
        </p>
      </div>
    );
  }

  const { charts, data_summary } = chartsData;

  // Fallbacks when summary missing
  const fallbackProfitStats = (() => {
    let records = data_summary?.total_records ?? null;
    let dateRange = data_summary?.date_range ?? null;

    // Infer records from profit_trend or revenue_profit_scatter
    const trend = charts?.profit_trend;
    const labels = trend?.data?.labels;
    const ds = trend?.data?.datasets?.[0]?.data;
    if (records == null) {
      if (Array.isArray(ds) && ds.length > 0) records = ds.length;
      else if (Array.isArray(labels) && labels.length > 0)
        records = labels.length;
    }
    if (!dateRange && Array.isArray(labels) && labels.length > 0) {
      const parsed = labels
        .map((v) => new Date(v))
        .filter((d) => !isNaN(d.getTime()))
        .sort((a, b) => a - b);
      if (parsed.length > 0) {
        dateRange = {
          start: parsed[0].toISOString(),
          end: parsed[parsed.length - 1].toISOString(),
        };
      }
    }
    return { records, dateRange };
  })();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>💰 Profit Analysis Dashboard</h1>
        <p>
          Comprehensive analysis of your profit margins, trends, and financial
          performance
        </p>

        {/* Standardized profit summary */}
        {stdSummary &&
          (() => {
            const summaries = stdSummary.summaries || {};
            const metrics = stdSummary.metrics || {};
            const profit = summaries.profit_analysis || {};
            const m = metrics.profit_analysis || {};
            const totalRevenue = profit.total_revenue ?? null;
            const totalProfit = profit.total_profit ?? null;
            const profitMargin = profit.profit_margin ?? null;
            const negCount = m.items_with_negative_margin ?? null;
            return (
              <div className="summary-cards" style={{ marginTop: "0.5rem" }}>
                {totalRevenue != null && (
                  <div className="summary-card">
                    <span className="summary-icon">💰</span>
                    <div className="summary-content">
                      <h3>₹{Number(totalRevenue).toLocaleString()}</h3>
                      <p>Total Revenue</p>
                    </div>
                  </div>
                )}
                {totalProfit != null && (
                  <div className="summary-card">
                    <span className="summary-icon">📈</span>
                    <div className="summary-content">
                      <h3>₹{Number(totalProfit).toLocaleString()}</h3>
                      <p>Total Profit</p>
                    </div>
                  </div>
                )}
                {profitMargin != null && (
                  <div className="summary-card">
                    <span className="summary-icon">📊</span>
                    <div className="summary-content">
                      <h3>{Number(profitMargin).toFixed(1)}%</h3>
                      <p>Profit Margin</p>
                    </div>
                  </div>
                )}
                {negCount != null && (
                  <div className="summary-card">
                    <span className="summary-icon">⚠️</span>
                    <div className="summary-content">
                      <h3>{Number(negCount).toLocaleString()}</h3>
                      <p>Negative-Margin Items</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-icon">📊</span>
            <div className="summary-content">
              <h3>
                {Number(fallbackProfitStats.records ?? 0).toLocaleString()}
              </h3>
              <p>Total Records</p>
            </div>
          </div>

          <div className="summary-card">
            <span className="summary-icon">📅</span>
            <div className="summary-content">
              <h3>
                {(() => {
                  const dr = fallbackProfitStats.dateRange;
                  if (dr?.start && dr?.end) {
                    try {
                      const startDate = new Date(dr.start).toLocaleDateString();
                      const endDate = new Date(dr.end).toLocaleDateString();
                      return `${startDate} - ${endDate}`;
                    } catch (e) {
                      return "Date range unavailable";
                    }
                  }
                  return "No date range";
                })()}
              </h3>
              <p>Analysis Period</p>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        {charts.profit_margin_distribution && (
          <Chart
            chartData={charts.profit_margin_distribution}
            title="Profit Margin Distribution"
          />
        )}
        {charts.revenue_profit_scatter && (
          <Chart
            chartData={charts.revenue_profit_scatter}
            title="Revenue vs Profit Analysis"
          />
        )}
        {charts.top_performers && (
          <Chart
            chartData={charts.top_performers}
            title="Top Profit Performers"
          />
        )}
        {charts.profit_by_category && (
          <Chart
            chartData={charts.profit_by_category}
            title="Profit by Category"
          />
        )}
        {charts.profit_trend && (
          <Chart
            chartData={charts.profit_trend}
            title="Profit Trend Over Time"
          />
        )}
        {charts.margin_heatmap && (
          <Chart
            chartData={charts.margin_heatmap}
            title="Profit Margin Heatmap"
          />
        )}
      </div>

      <div className="dashboard-actions">
        <button onClick={fetchProfitData} className="refresh-btn">
          🔄 Refresh Data
        </button>
      </div>
    </div>
  );
}

export default ProfitDashboard;
